/**
 * Recibir Matrículas DGII — migración web de ares6050.
 *
 * Recepción física inicial de la placa que llega de la DGII (distinta de la
 * transferencia de documentos entre sucursales): asigna placa, endoso,
 * sucursal de matrícula (la del usuario) y la localización física
 * (correlativo asignado por el servidor), y marca el vehículo en inventario.
 * Para matrículas ya recibidas permite corregir placa/endoso sin tocar la
 * localización. El cobro de la matrícula se muestra como información pero no
 * bloquea la recepción (igual que el escritorio).
 *
 * Modo "por solicitud" (lote): se elige una solicitud de matrícula viva con
 * chasis pendientes, se completa placa/endoso por línea y se reciben todos
 * en una sola transacción del servidor (todo o nada). Cada línea se cierra
 * en la solicitud y la cabecera se cierra sola al recibir la última.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import PropTypes from "prop-types";
import CircularProgress from "@mui/material/CircularProgress";
import InventoryOutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { labelTipo, useTiposDocumento, codigoSucursal } from "lib/documentosvehiculo";
import FormControlLabel from "@mui/material/FormControlLabel";

/** Tipos que DGII entrega siempre con la matrícula: marcados por defecto.
 *  Las copias las saca el dealer, así que arrancan desmarcadas. */
const TIPOS_POR_DEFECTO = new Set(["placa", "matricula"]);

/**
 * Checkboxes de los documentos físicos que llegan con la matrícula. Lo que
 * se marque queda ubicado en el blob del producto (sucursal de matrícula),
 * que desde ahora dice dónde está cada documento: Revisión deja de mostrar
 * "sin registrar" y los envíos entre sucursales parten de aquí.
 */
function DocumentosRecibidos({ tipos, seleccion, onChange, registrados }) {
  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.4}>
      {tipos.map((t) => {
        const ya = registrados.find((d) => d.documento === t.value);
        return (
          <FormControlLabel
            key={t.value}
            control={
              <Checkbox
                size="small"
                checked={seleccion.includes(t.value)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...seleccion, t.value]
                      : seleccion.filter((x) => x !== t.value)
                  )
                }
              />
            }
            label={
              <Typography component="span" sx={{ fontSize: "0.84rem", fontWeight: 700 }}>
                {t.label}
                {ya && (
                  <Typography
                    component="span"
                    sx={{
                      ml: 0.6,
                      fontSize: "0.72rem",
                      color: FP.color.textMuted,
                      fontWeight: 400,
                    }}
                  >
                    (ya en {ya.sucursalrecep || "sucursal"})
                  </Typography>
                )}
              </Typography>
            }
          />
        );
      })}
    </Stack>
  );
}

DocumentosRecibidos.propTypes = {
  tipos: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }))
    .isRequired,
  seleccion: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  registrados: PropTypes.arrayOf(PropTypes.shape({ documento: PropTypes.string })),
};

DocumentosRecibidos.defaultProps = { registrados: [] };

const porDefecto = (tipos) => tipos.map((t) => t.value).filter((v) => TIPOS_POR_DEFECTO.has(v));

const filtrarProductos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string" ? o : `${o.chasis} ${o.marca || ""} ${o.modelo || ""} ${o.colore || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

const lineaChip = (l) => {
  if (l.yarecibido)
    return (
      <Chip
        size="small"
        label="Ya recibido"
        sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
      />
    );
  if (l.estadolinea === "observada")
    return (
      <Chip
        size="small"
        label="Observada"
        title={l.motivoobservacion}
        sx={{ bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 700 }}
      />
    );
  return (
    <Chip
      size="small"
      label="Pendiente"
      sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
    />
  );
};

/** Recepción por solicitud (lote): todas las líneas pendientes de una vez. */
function RecepcionPorLote({ post, showAlert, lotes, cargandoLotes, cargarLotes, loteInicial }) {
  const [lote, setLote] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [cargandoLineas, setCargandoLineas] = useState(false);
  const [recibiendo, setRecibiendo] = useState(false);
  const { tipos: tiposDoc } = useTiposDocumento();
  const [docsLote, setDocsLote] = useState([]);
  useEffect(() => {
    setDocsLote(porDefecto(tiposDoc));
  }, [tiposDoc]);

  const cargarLineas = async (id) => {
    setCargandoLineas(true);
    try {
      const { ok, data } = await post("/api/recepcionmatricula_lote_datos", { id });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo cargar la solicitud", color: "error" });
        setLineas([]);
        return;
      }
      // Marcadas por defecto todas las que se pueden recibir.
      setLineas(
        (data.lineas || []).map((l) => ({
          ...l,
          incluir: !l.yarecibido,
          placa: l.placa || "",
          endoso: l.endoso || "",
        }))
      );
    } finally {
      setCargandoLineas(false);
    }
  };

  // Lote preseleccionado desde el panel de inicio (clic en una solicitud).
  useEffect(() => {
    if (loteInicial) {
      setLote(loteInicial);
      cargarLineas(loteInicial.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loteInicial]);

  const editar = (lineaId, campo, valor) =>
    setLineas((prev) => prev.map((l) => (l.linea_id === lineaId ? { ...l, [campo]: valor } : l)));

  const seleccionadas = lineas.filter((l) => l.incluir && !l.yarecibido);
  const recibibles = lineas.filter((l) => !l.yarecibido);
  const todas = recibibles.length > 0 && seleccionadas.length === recibibles.length;

  const recibir = async () => {
    setRecibiendo(true);
    try {
      const { ok, data } = await post("/api/recepcionmatricula_recibirlote", {
        id: lote.id,
        items: seleccionadas.map((l) => ({ chasis: l.chasis, placa: l.placa, endoso: l.endoso })),
        documentos: docsLote,
      });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo recibir el lote", color: "error" });
        return;
      }
      showAlert({ message: data.mensajeok, color: "success" });
      setLote(null);
      setLineas([]);
      cargarLotes();
    } finally {
      setRecibiendo(false);
    }
  };

  const celda = { py: 0.6, px: 1, fontSize: "0.82rem", borderColor: FP.color.border };

  return (
    <>
      <Box sx={sectionBox}>
        <SectionTitle icon={ListAltOutlinedIcon} color={FP.color.accentBlue}>
          Solicitud de matrícula
        </SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={lotes}
              loading={cargandoLotes}
              value={lote}
              onChange={(_, v) => {
                setLote(v);
                setLineas([]);
                if (v) cargarLineas(v.id);
              }}
              getOptionLabel={(o) =>
                `#${o.id} · ${o.tipo === "dgii" ? "DGII" : "Proveedor"} · ${
                  o.pendientes
                } pendiente(s)`
              }
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                      #{o.id} · {o.tipo === "dgii" ? "DGII" : "Proveedor"} · {o.pendientes}/
                      {o.total} pendiente(s)
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                      {[
                        o.fecha_crea,
                        o.servicio,
                        o.destino,
                        o.gestor && `Gestor: ${o.gestor}`,
                        o.numerodocumento && `Aut. ${o.numerodocumento}`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Solicitud con chasis pendientes"
                  helperText={
                    !cargandoLotes && lotes.length === 0
                      ? "No hay solicitudes con chasis pendientes de recibir"
                      : ""
                  }
                  sx={fpInputSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
            {lote && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={lote.estado}
                  sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }}
                />
                {lote.servicio && (
                  <Chip
                    label={lote.servicio}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                )}
                {lote.numerodocumento && (
                  <Chip
                    label={`Autorización ${lote.numerodocumento}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                )}
              </Stack>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Sin lote elegido: las solicitudes con pendientes como tarjetas, para
          elegir de un vistazo (y que la pantalla no quede vacía). */}
      {!lote && (
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={BadgeOutlinedIcon} color={FP.color.success}>
            Solicitudes con chasis pendientes ({lotes.length})
          </SectionTitle>
          {cargandoLotes && (
            <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!cargandoLotes && lotes.length === 0 && (
            <Typography sx={{ fontSize: "0.82rem", color: FP.color.textMuted }}>
              Ninguna solicitud de matrícula tiene chasis pendientes de recibir. Las solicitudes se
              crean en Matrículas → Solicitudes DGII/Proveedor.
            </Typography>
          )}
          {!cargandoLotes && lotes.length > 0 && (
            <Grid container spacing={1.6}>
              {lotes.map((l) => {
                const pct = l.total ? Math.round(((l.total - l.pendientes) / l.total) * 100) : 0;
                return (
                  <Grid item xs={12} sm={6} md={4} key={l.id}>
                    <Box
                      onClick={() => {
                        setLote(l);
                        setLineas([]);
                        cargarLineas(l.id);
                      }}
                      sx={{
                        border: `1px solid ${FP.color.border}`,
                        borderRadius: 2,
                        p: 1.8,
                        height: "100%",
                        cursor: "pointer",
                        bgcolor: FP.color.surface,
                        transition: "border-color .15s, box-shadow .15s",
                        "&:hover": { borderColor: FP.color.accentBlue, boxShadow: FP.shadow.soft },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.6,
                        }}
                      >
                        <Typography sx={{ fontWeight: 800, fontSize: "0.9rem" }}>
                          #{l.id} · {l.tipo === "dgii" ? "DGII" : "Proveedor"}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${l.pendientes} pendiente(s)`}
                          sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                        />
                      </Box>
                      <Typography
                        sx={{ fontSize: "0.8rem", fontWeight: 700, color: FP.color.textSecondary }}
                      >
                        {l.servicio ||
                          (l.tipo === "dgii" ? "Trámite DGII" : "Retiro de documentos")}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted, mb: 1 }}>
                        {[
                          l.fecha_crea,
                          l.destino,
                          l.gestor && `Gestor: ${l.gestor}`,
                          l.numerodocumento && `Aut. ${l.numerodocumento}`,
                          l.fechalimitepago && `Pago vence ${l.fechalimitepago}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            flex: 1,
                            height: 6,
                            borderRadius: 3,
                            bgcolor: FP.color.surfaceAlt,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{ width: `${pct}%`, height: "100%", bgcolor: FP.color.success }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            color: FP.color.textSecondary,
                          }}
                        >
                          {l.total - l.pendientes}/{l.total} recibidos
                        </Typography>
                      </Box>
                      {l.observados > 0 && (
                        <Typography
                          sx={{ mt: 0.6, fontSize: "0.72rem", color: "#C62828", fontWeight: 700 }}
                        >
                          {l.observados} observado(s) por DGII
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {lote && (
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <SectionTitle icon={BadgeOutlinedIcon} color={FP.color.success}>
              Chasis a recibir ({seleccionadas.length} de {recibibles.length})
            </SectionTitle>
            <Button
              startIcon={
                recibiendo ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <InventoryOutlinedIcon />
                )
              }
              onClick={recibir}
              disabled={recibiendo || cargandoLineas || seleccionadas.length === 0}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Recibir {seleccionadas.length} matrícula(s)
            </Button>
          </Box>
          {cargandoLineas ? (
            <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 760 }}>
                {/* El tema (assets/theme/components/table/tableHead) pone
                    display:block al TableHead y desalinea las columnas. */}
                <TableHead sx={{ display: "table-header-group", p: 0, borderRadius: 0 }}>
                  <TableRow>
                    <TableCell padding="checkbox" sx={celda}>
                      <Checkbox
                        size="small"
                        checked={todas}
                        indeterminate={seleccionadas.length > 0 && !todas}
                        disabled={recibibles.length === 0}
                        onChange={(e) =>
                          setLineas((prev) =>
                            prev.map((l) =>
                              l.yarecibido ? l : { ...l, incluir: e.target.checked }
                            )
                          )
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ ...celda, fontWeight: 800 }}>Chasis</TableCell>
                    <TableCell sx={{ ...celda, fontWeight: 800 }}>Vehículo</TableCell>
                    <TableCell sx={{ ...celda, fontWeight: 800 }}>Estado</TableCell>
                    <TableCell sx={{ ...celda, fontWeight: 800, width: 170 }}>Placa</TableCell>
                    <TableCell sx={{ ...celda, fontWeight: 800, width: 170 }}>Endoso</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineas.map((l) => (
                    <TableRow key={l.linea_id} sx={{ opacity: l.yarecibido ? 0.55 : 1 }}>
                      <TableCell padding="checkbox" sx={celda}>
                        <Checkbox
                          size="small"
                          checked={Boolean(l.incluir) && !l.yarecibido}
                          disabled={l.yarecibido}
                          onChange={(e) => editar(l.linea_id, "incluir", e.target.checked)}
                        />
                      </TableCell>
                      <TableCell sx={{ ...celda, fontFamily: "monospace", fontWeight: 700 }}>
                        {l.chasis}
                      </TableCell>
                      <TableCell sx={celda}>
                        {[l.marca, l.modelo, l.colore].filter(Boolean).join(" · ")}
                      </TableCell>
                      <TableCell sx={celda}>{lineaChip(l)}</TableCell>
                      <TableCell sx={celda}>
                        <TextField
                          size="small"
                          fullWidth
                          value={l.placa}
                          disabled={l.yarecibido}
                          onChange={(e) =>
                            editar(l.linea_id, "placa", e.target.value.toUpperCase())
                          }
                          inputProps={{ style: { fontFamily: "monospace", fontWeight: 700 } }}
                          sx={fpInputSx}
                        />
                      </TableCell>
                      <TableCell sx={celda}>
                        <TextField
                          size="small"
                          fullWidth
                          value={l.endoso}
                          disabled={l.yarecibido}
                          onChange={(e) => editar(l.linea_id, "endoso", e.target.value)}
                          sx={fpInputSx}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {lineas.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ ...celda, textAlign: "center", color: FP.color.textMuted }}
                      >
                        La solicitud no tiene chasis pendientes
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
          <Box sx={{ mt: 2, pt: 1.6, borderTop: `1px solid ${FP.color.border}` }}>
            <Typography
              sx={{ fontSize: "0.78rem", fontWeight: 700, color: FP.color.textSecondary, mb: 0.4 }}
            >
              Documentos que llegan con cada matrícula del lote
            </Typography>
            <DocumentosRecibidos tipos={tiposDoc} seleccion={docsLote} onChange={setDocsLote} />
          </Box>
          <Typography sx={{ mt: 1.2, fontSize: "0.75rem", color: FP.color.textMuted }}>
            La recepción del lote es todo o nada: si un chasis no se puede recibir, no se recibe
            ninguno y se indica cuál. La ubicación física se asigna correlativa a cada uno.
          </Typography>
        </Box>
      )}
    </>
  );
}

RecepcionPorLote.propTypes = {
  post: PropTypes.func.isRequired,
  showAlert: PropTypes.func.isRequired,
  lotes: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
  cargandoLotes: PropTypes.bool.isRequired,
  cargarLotes: PropTypes.func.isRequired,
  loteInicial: PropTypes.shape({ id: PropTypes.number }),
};

RecepcionPorLote.defaultProps = {
  loteInicial: null,
};

/** Tarjeta de cifra del panel de inicio. */
function Kpi({ valor, etiqueta, color, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        ...sectionBox,
        mb: 0,
        p: 2,
        display: "flex",
        alignItems: "center",
        gap: 1.6,
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { borderColor: color } : undefined,
      }}
    >
      <Box
        sx={{
          minWidth: 56,
          height: 56,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
          fontWeight: 800,
          fontSize: "1.35rem",
        }}
      >
        {valor}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: "0.84rem", color: FP.color.textSecondary }}>
        {etiqueta}
      </Typography>
    </Box>
  );
}

Kpi.propTypes = {
  valor: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  etiqueta: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

Kpi.defaultProps = {
  onClick: null,
};

const filaLista = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  px: 1.2,
  py: 0.9,
  borderRadius: 1.6,
  cursor: "pointer",
  "&:hover": { bgcolor: FP.color.surface },
};

export default function RecibirMatriculaDgii() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [opts, setOpts] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [sel, setSel] = useState(null);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [placa, setPlaca] = useState("");
  const [endoso, setEndoso] = useState("");
  const { tipos: tiposDoc } = useTiposDocumento();
  const [docsRecibidos, setDocsRecibidos] = useState([]);
  const [guardando, setGuardando] = useState(false);
  // "chasis" = recepción suelta (flujo original); "lote" = por solicitud.
  const [modo, setModo] = useState("chasis");
  const [lotes, setLotes] = useState([]);
  const [cargandoLotes, setCargandoLotes] = useState(false);
  const [loteInicial, setLoteInicial] = useState(null);

  const cargarLotes = async () => {
    setCargandoLotes(true);
    try {
      const { ok, data } = await post("/api/recepcionmatricula_lotes", {});
      setLotes(ok && Array.isArray(data.lotes) ? data.lotes : []);
    } finally {
      setCargandoLotes(false);
    }
  };

  useEffect(() => {
    cargarLotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Panel de inicio: lo que el sistema ya sabe de las matrículas.
  const pendientes = opts.filter(
    (o) => (o.estado || "").toUpperCase() === "V" && (o.inventario || "").toUpperCase() !== "S"
  );
  const recibidas = opts.filter((o) => (o.inventario || "").toUpperCase() === "S");
  const chasisEnLotes = lotes.reduce((acc, l) => acc + (l.pendientes || 0), 0);

  useEffect(() => {
    let activo = true;
    setBuscando(true);
    post("/api/recepcionmatricula_productos", {})
      .then(({ ok, data }) => {
        if (activo && ok && Array.isArray(data.productos)) setOpts(data.productos);
      })
      .finally(() => activo && setBuscando(false));
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limpiar = () => {
    setSel(null);
    setDatos(null);
    setPlaca("");
    setEndoso("");
  };

  const cargarDatos = async (chasis) => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/recepcionmatricula_datos", { chasis });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo cargar el producto", color: "error" });
        limpiar();
        return;
      }
      setDatos(data);
      setPlaca(data.placa || "");
      setEndoso(data.endoso || "");
      // Propuesta: los de siempre (placa/matrícula) más lo que ya esté
      // registrado en esta sucursal, para no "mover" nada sin querer.
      const ya = (data.documentos || [])
        .filter((d) => codigoSucursal(d.sucursalrecep) === codigoSucursal(data.sucursal_label))
        .map((d) => d.documento);
      setDocsRecibidos([...new Set([...porDefecto(tiposDoc), ...ya])]);
    } finally {
      setCargando(false);
    }
  };

  const ejecutar = async (endpoint, exito) => {
    setGuardando(true);
    try {
      const { ok, data } = await post(endpoint, {
        chasis: datos.chasis,
        placa: placa.trim(),
        endoso: endoso.trim(),
        documentos: docsRecibidos,
      });
      if (ok) {
        showAlert({
          message:
            (data.mensajeok || exito) +
            (data.localizacion ? ` — ubicación asignada: ${data.localizacion}` : ""),
          color: "success",
        });
        cargarDatos(datos.chasis);
      } else {
        showAlert({ message: data.Error || "Error al guardar", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Recibir Matrículas DGII"
        subtitle="Registra la llegada física de la placa: ubicación, placa y endoso"
        cta={
          <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
            Nuevo
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <ToggleButtonGroup
          exclusive
          value={modo}
          onChange={(_, v) => v && setModo(v)}
          size="small"
          sx={{
            mb: 2.4,
            bgcolor: FP.color.cardBg,
            "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 700, px: 2 },
          }}
        >
          <ToggleButton value="chasis">
            <InventoryOutlinedIcon sx={{ fontSize: 18, mr: 0.8 }} /> Por chasis
          </ToggleButton>
          <ToggleButton value="lote">
            <ListAltOutlinedIcon sx={{ fontSize: 18, mr: 0.8 }} /> Por solicitud (lote)
          </ToggleButton>
        </ToggleButtonGroup>

        {modo === "lote" && (
          <RecepcionPorLote
            post={post}
            showAlert={showAlert}
            lotes={lotes}
            cargandoLotes={cargandoLotes}
            cargarLotes={cargarLotes}
            loteInicial={loteInicial}
          />
        )}

        {/* Vehículo */}
        {modo === "chasis" && (
          <Box sx={sectionBox}>
            <SectionTitle icon={DirectionsCarFilledOutlinedIcon} color={FP.color.accentBlue}>
              Vehículo
            </SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={5}>
                <Autocomplete
                  autoHighlight
                  selectOnFocus
                  handleHomeEndKeys
                  filterOptions={filtrarProductos}
                  options={opts}
                  loading={buscando || cargando}
                  value={sel}
                  getOptionLabel={(o) =>
                    typeof o === "string"
                      ? o
                      : `${o.chasis} — ${[o.marca, o.modelo].filter(Boolean).join(" ")}`
                  }
                  isOptionEqualToValue={(o, v) => (o.chasis || o) === (v.chasis || v)}
                  onChange={(_, v) => {
                    setSel(v);
                    setDatos(null);
                    if (v?.chasis) cargarDatos(v.chasis);
                  }}
                  renderOption={(props, o) => (
                    <li {...props} key={o.chasis}>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                          {o.chasis}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                          {[o.marca, o.modelo, o.colore].filter(Boolean).join(" · ")}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Chasis, marca o modelo" sx={fpInputSx} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={7} sx={{ display: "flex", alignItems: "center" }}>
                {datos && (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {datos.recibida ? (
                      <Chip
                        label="Recibida"
                        sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
                      />
                    ) : (
                      <Chip
                        label="Pendiente de recibir"
                        sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                      />
                    )}
                    {datos.tipoventa && (
                      <Chip
                        label={datos.tipoventa}
                        sx={{
                          bgcolor: FP.color.surfaceAlt,
                          color: FP.color.textSecondary,
                          fontWeight: 700,
                        }}
                      />
                    )}
                    {datos.solicitud && (
                      <Chip
                        label={`En solicitud #${datos.solicitud.solicitud_id} (${
                          datos.solicitud.estado
                        }${
                          datos.solicitud.fechalimitepago
                            ? `, pago vence ${datos.solicitud.fechalimitepago}`
                            : ""
                        }) — al recibir se cierra la línea`}
                        sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }}
                      />
                    )}
                    {datos.cobro ? (
                      <Chip
                        label={`${
                          datos.cobro.tipodocumento
                            ? labelTipo(datos.cobro.tipodocumento)
                            : "Matrícula"
                        } pagada el ${datos.cobro.fecha} (RD$ ${fmtMoney(datos.cobro.valor)})`}
                        sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }}
                      />
                    ) : (
                      <Chip
                        label="Sin pago de matrícula registrado"
                        sx={{
                          bgcolor: FP.color.surfaceAlt,
                          color: FP.color.textSecondary,
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </Stack>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Panel de inicio: sin vehículo seleccionado, la pantalla muestra el
            estado general y atajos (elegir un pendiente o saltar a un lote). */}
        {modo === "chasis" && !datos && !cargando && (
          <>
            <Grid container spacing={2} sx={{ mb: 2.4 }}>
              <Grid item xs={12} sm={4}>
                <Kpi
                  valor={buscando ? "…" : pendientes.length}
                  etiqueta="Vendidos pendientes de recibir"
                  color="#E65100"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Kpi
                  valor={cargandoLotes ? "…" : chasisEnLotes}
                  etiqueta={`En ${lotes.length} solicitud(es) abierta(s)`}
                  color={FP.color.accentBlue}
                  onClick={lotes.length ? () => setModo("lote") : null}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Kpi
                  valor={buscando ? "…" : recibidas.length}
                  etiqueta="Matrículas recibidas en inventario"
                  color={FP.color.success}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2.4}>
              <Grid item xs={12} md={7}>
                <Box sx={{ ...sectionBox, mb: 0, height: "100%" }}>
                  <SectionTitle icon={BadgeOutlinedIcon} color="#E65100">
                    Pendientes de recibir
                  </SectionTitle>
                  {buscando && (
                    <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  {!buscando && pendientes.length === 0 && (
                    <Typography sx={{ fontSize: "0.82rem", color: FP.color.textMuted }}>
                      No hay vehículos vendidos con matrícula pendiente de recibir.
                    </Typography>
                  )}
                  {!buscando && pendientes.length > 0 && (
                    <>
                      {pendientes.slice(0, 8).map((o) => (
                        <Box
                          key={o.chasis}
                          sx={filaLista}
                          onClick={() => {
                            setSel(o);
                            cargarDatos(o.chasis);
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{ fontWeight: 700, fontSize: "0.84rem", fontFamily: "monospace" }}
                            >
                              {o.chasis}
                            </Typography>
                            <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                              {[o.marca, o.modelo, o.colore].filter(Boolean).join(" · ")}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={Number(o.noprest) ? "Crédito" : "Contado"}
                            sx={{
                              bgcolor: FP.color.surfaceAlt,
                              color: FP.color.textSecondary,
                              fontWeight: 700,
                            }}
                          />
                        </Box>
                      ))}
                      {pendientes.length > 8 && (
                        <Typography
                          sx={{ mt: 1, px: 1.2, fontSize: "0.75rem", color: FP.color.textMuted }}
                        >
                          y {pendientes.length - 8} más — búscalos arriba por chasis, marca o
                          modelo.
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={{ ...sectionBox, mb: 0, height: "100%" }}>
                  <SectionTitle icon={ListAltOutlinedIcon} color={FP.color.accentBlue}>
                    Solicitudes con chasis pendientes
                  </SectionTitle>
                  {cargandoLotes && (
                    <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  {!cargandoLotes && lotes.length === 0 && (
                    <Typography sx={{ fontSize: "0.82rem", color: FP.color.textMuted }}>
                      Ninguna solicitud de matrícula tiene chasis pendientes.
                    </Typography>
                  )}
                  {!cargandoLotes &&
                    lotes.length > 0 &&
                    lotes.slice(0, 8).map((l) => (
                      <Box
                        key={l.id}
                        sx={filaLista}
                        onClick={() => {
                          setLoteInicial(l);
                          setModo("lote");
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.84rem" }}>
                            #{l.id} · {l.tipo === "dgii" ? "DGII" : "Proveedor"}
                            {l.servicio ? ` · ${l.servicio}` : ""}
                          </Typography>
                          <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                            {[
                              l.fecha_crea,
                              l.gestor && `Gestor: ${l.gestor}`,
                              l.numerodocumento && `Aut. ${l.numerodocumento}`,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={`${l.pendientes} de ${l.total}`}
                          sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                        />
                      </Box>
                    ))}
                </Box>
              </Grid>
            </Grid>
          </>
        )}

        {/* Recepción */}
        {modo === "chasis" && datos && (
          <Box sx={{ ...sectionBox, mb: 0 }}>
            <SectionTitle icon={BadgeOutlinedIcon} color={FP.color.success}>
              Datos de la Matrícula
            </SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Ubicación física"
                  value={datos.localizacion || ""}
                  helperText={datos.recibida ? "Asignada" : "Propuesta (la confirma el servidor)"}
                  InputProps={{ readOnly: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Placa"
                  value={placa}
                  onChange={(e) => setPlaca(e.target.value.toUpperCase())}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Endoso"
                  value={endoso}
                  onChange={(e) => setEndoso(e.target.value)}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Sucursal matrícula"
                  value={datos.sucmatricula || datos.sucursal_label || ""}
                  InputProps={{ readOnly: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: FP.color.textSecondary,
                    mb: 0.4,
                  }}
                >
                  {datos.recibida
                    ? "Documentos registrados en esta sucursal"
                    : "Documentos que llegan con la matrícula"}
                </Typography>
                {datos.recibida ? (
                  <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                    {(datos.documentos || []).length === 0 && (
                      <Typography sx={{ fontSize: "0.8rem", color: FP.color.textMuted }}>
                        Ninguno registrado (recibida antes de que se registraran documentos).
                      </Typography>
                    )}
                    {(datos.documentos || []).map((d) => (
                      <Chip
                        key={d.documento}
                        size="small"
                        label={`${labelTipo(d.documento)} · ${
                          d.entregado ? "entregado al cliente" : d.sucursalrecep || "sin sucursal"
                        }`}
                        sx={{
                          bgcolor: d.entregado ? "#E8F5E9" : "#E3F2FD",
                          color: d.entregado ? "#2E7D32" : "#1565C0",
                          fontWeight: 700,
                        }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <DocumentosRecibidos
                    tipos={tiposDoc}
                    seleccion={docsRecibidos}
                    onChange={setDocsRecibidos}
                    registrados={datos.documentos || []}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1.2}>
                  {!datos.recibida && (
                    <Button
                      startIcon={
                        guardando ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <InventoryOutlinedIcon />
                        )
                      }
                      onClick={() =>
                        ejecutar("/api/recepcionmatricula_recibir", "Matrícula recibida")
                      }
                      disabled={guardando}
                      sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                    >
                      Recibir matrícula
                    </Button>
                  )}
                  {datos.recibida && (
                    <Button
                      startIcon={
                        guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                      }
                      onClick={() =>
                        ejecutar("/api/recepcionmatricula_modificar", "Datos actualizados")
                      }
                      disabled={guardando}
                      sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                    >
                      Guardar cambios
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
}
